import React from "react";
import { useNavigate } from "react-router-dom";
import { getActuatorColor, getFormattedThreshold, getActuatorValue, getActuatorUnit, getActuatorHigh, getActuatorLow } from "../../utils/actionUtils";
import PropTypes from "prop-types";
import { useCards } from "../../utils/reactQueryHooks";
import { useCardData } from '../common/CardContext';
import { checkMqttCard } from '../../utils/cardUtils';
import { getSampleImagesForDevice } from '../../utils/sampleImageUtils';
import WateringStatus from './WateringStatus';

import defaultImage from "../../assets/images/default_dashboard_img.jpg";
import irrigationImage from "../../assets/images/irrigation.png";
import humidityImage from "../../assets/images/humidity.png";
import temperatureImage from "../../assets/images/temperature.png";
import lightImage from "../../assets/images/light.png";
import fanImage from "../../assets/images/fan.png";
import { processTimeStamp } from "../../utils/chartUtils";
import { useActuatorPresence } from "./ActuatorPresence";
import { formatAge } from "../../utils/timeUtils";

const normalizeMacAddress = (mac) =>
    typeof mac === "string" ? mac.trim().replace(/;/g, ":").toUpperCase() : "";

export default function Action({ action, onDelete, showMacAddress = false }) {
    //Pass in navigation control so we can navigate out of this card if it's deleted.
    const navigate = useNavigate();
    
    // Use React Query hook to get all cards
    const { data: cards = [] } = useCards();
    
    // Find the card matching the action's sensor MAC address
    const pairedSensorMac = normalizeMacAddress(action.sensor);
    const card = cards.find(
        (candidate) => normalizeMacAddress(candidate.macAddress) === pairedSensorMac
    );
    const actuatorUnit = getActuatorUnit(action.actuatorType);
    const actuatorHigh = card ? getActuatorHigh(action.actuatorType, card) : 100;
    const actuatorLow = card ? getActuatorLow(action.actuatorType, card) : 0;
    
    // Get real-time MQTT data from context
    const { cardData } = useCardData();
    const isMqttCard = checkMqttCard(card?.macAddress);

    const getActuatorValueFromData = (latest) => {
        if (!latest) return null;

        switch (action.actuatorType) {
            case 'temperature':
                return latest.mostRecentTemp;
            case 'humidity':
                return latest.mostRecentHumidity;
            case 'irrigation':
                return latest.mostRecentDistance;
            case 'fan':
                return latest.mostRecentHumidity;
            case 'light':
                return latest.mostRecentLight;
            case 'camera':
                return latest.mostRecentLight; // Use light sensor for camera trigger
            default:
                return null;
        }
    }
    
    // Safely get MQTT data using optional chaining to prevent errors
    const latestData = isMqttCard && card?.macAddress ? cardData[card.macAddress] : null;
    const presence = useActuatorPresence(action.macAddress, action.actuatorType);
    const presenceAge = presence?.lastSeenAt != null ? Date.now() - presence.lastSeenAt : null;
    const presenceLabel = !presence
        ? null
        : presence.status === "online"
            ? `Online${presenceAge != null ? ` · ${formatAge(presenceAge) || "just now"}` : ""}`
            : presence.status === "offline"
                ? presenceAge != null
                    ? `Offline · last seen ${formatAge(presenceAge)}`
                    : "Offline"
                : "Checking";
    const presenceClass = presence?.status === "online"
        ? "card-status-online"
        : presence?.status === "offline"
            ? "card-status-offline"
            : "card-status-unknown";
    
    // Get the actuator value with fallbacks
    const displayValue = getActuatorValueFromData(latestData) ?? 
                         getActuatorValue(action.actuatorType, card) ?? 
                         "--";
    const currentTimestamp = latestData?.mostRecentTimestamp ?? null;

    // Function to get camera image with fallback logic
    const getCameraImage = () => {
        // Check if action has a latest image URL
        if (action.latestImageUrl) {
            // Check if the image timestamp is within the last day
            const imageTimestamp = action.imageTimestamp;
            if (imageTimestamp) {
                const imageDate = new Date(imageTimestamp);
                const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
                
                if (imageDate > oneDayAgo) {
                    return action.latestImageUrl;
                }
            }
        }
        
        // Fallback to sample camera image
        return getSampleImagesForDevice('camera').primary;
    };

    //Function to map actuator types to their respective images:
    const getActuatorImage = (actuatorType) => {
        // Use sample image utility for consistent image selection
        const sampleImages = getSampleImagesForDevice(actuatorType);
        return sampleImages.primary || defaultImage;
    }

    //Function to handle the card click and navigate to appropriate wrapper
    const handleCardClick = () => {
        if (action.actuatorType === 'camera') {
            navigate(`/camera/${action.macAddress}`, {
                state: { action: action }
            });
        } else {
            navigate(`/actions/${action.macAddress}`, {
                state: { action: action }
            });
        }
    }

    //Function to handle the delete button click:
    const handleDeleteClick = (e) => {
        //Preven the click from triggering the card navigation.
        e.stopPropagation();
        //Call the onDelete function passed down from the actions dashboard.
        onDelete(action.macAddress);
    }

    // Function to handle hover effects
    const handleMouseEnter = (e) => {
        const card = e.currentTarget;
        const actuatorColor = getActuatorColor(action.actuatorType);
        card.style.boxShadow = `0 4px 8px rgba(0, 0, 0, 0.2), 0 0 8px ${actuatorColor}`;
    };

    const handleMouseLeave = (e) => {
        const card = e.currentTarget;
        const actuatorColor = getActuatorColor(action.actuatorType);
        card.style.boxShadow = `0 4px 8px rgba(0, 0, 0, 0.2), 0 0 4px ${actuatorColor}`;
    };

    //What would we even like on the card?
    return (
        <div 
            className="action" 
            onClick={handleCardClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{
                borderColor: getActuatorColor(action.actuatorType),
                boxShadow: `0 4px 8px rgba(0, 0, 0, 0.2), 0 0 4px ${getActuatorColor(action.actuatorType)}`
            }}
        >
            <div className="action-header">
                <h3 className="card-description">{action.actuatorDescription}</h3>
                {showMacAddress && action.macAddress && (
                    <p className="action-mac-address" title={action.macAddress}>
                        {action.macAddress}
                    </p>
                )}
            </div>

            <div className="card-image-container">
                {action.actuatorType === 'camera' ? (
                    <img src={getCameraImage()} alt="Camera snapshot" className="card-image" />
                ) : (
                    <img src={getActuatorImage(action.actuatorType)} alt={`${action.actuatorType}`} className="card-image" />
                )}
            </div>

            <div className="card-data">
                {presenceLabel && (
                    <div
                        className={`card-status-indicator ${presenceClass}`}
                        title={presence?.source === "heartbeat" ? "From the device heartbeat" : "Checked while this dashboard is open"}
                    >
                        <span className="card-status-dot" aria-hidden="true"></span>
                        {presenceLabel}
                    </div>
                )}
                {action.actuatorType === 'camera' ? (
                    // Camera cards only show timestamp
                    <div className="card-timestamp">
                        <div className="card-paragraph-smaller">{currentTimestamp ?? processTimeStamp(new Date().toISOString())}</div>
                    </div>
                ) : (
                    // Other action types show value and thresholds
                    <>
                        <div className="card-temp-data">
                            <p className="card-paragraph" style={{color: getActuatorColor(action.actuatorType)}}>
                                {(displayValue || displayValue == 0) ? `${displayValue} ${actuatorUnit}` : '0.00'}</p>
                            <div className="action-range">
                                <p className="card-paragraph-small"><span style={{color: "#ADD8E6"}}>L: </span>{actuatorLow ? getFormattedThreshold(action.actuatorType, card, action.timezone).low : "_"} {actuatorUnit}</p>
                                <p className="card-paragraph-small"><span style={{color: "red"}}>H: </span>{actuatorHigh ? getFormattedThreshold(action.actuatorType, card, action.timezone).high : "_"} {actuatorUnit}</p>
                            </div>
                        </div>
                        <div className="card-timestamp">
                            <div className="card-paragraph-smaller">{currentTimestamp ?? processTimeStamp(new Date().toISOString())}</div>
                            {(action.actuatorType === 'irrigation' || action.actuatorType === 'irrigation2') && (
                                <WateringStatus actionMac={action.macAddress} variant="tile" />
                            )}
                        </div>
                    </>
                )}
            </div>


            <button className="card-delete-button" onClick={handleDeleteClick}>
                <i className="ri-close-large-line custom-close-icon"></i>
            </button>
        </div>
    )
}

//Prop validation:
Action.propTypes = 
{
    action: PropTypes.shape({
        sensor: PropTypes.string,
        macAddress: PropTypes.string,
        actuatorDescription: PropTypes.string,
        actuatorType: PropTypes.oneOf(['irrigation', 'irrigation2', 'temperature', 'fan', 'light', 'humidity', 'camera']),
        pwm: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
        duration: PropTypes.number,
        latestImageUrl: PropTypes.string,
        imageTimestamp: PropTypes.string
    }),
    onDelete: PropTypes.func.isRequired,
    showMacAddress: PropTypes.bool,
}